import React, { PropsWithChildren, useState } from "react";
import { getErrorMessage } from "@remhealth/host";
import { Button, DynamicImportError, isAbortError } from "@remhealth/ui";
import { Illustrations } from "~/assets";
import { ErrorDetailsDialog } from "./errorDetailsDialog";
import { ErrorContainer, Illustration } from "./errorBoundary.styles";

export interface ErrorBoundaryProps extends PropsWithChildren {
  mountKey?: string;
  onError?: (error: Error) => void;
}

interface ErrorBoundaryState {
  error?: Error;
  mountKey?: string;
}

/**
 * A boundary that catches errors.
 */
export class ErrorBoundary extends React.PureComponent<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { error: undefined, mountKey: props.mountKey };
  }

  public static getDerivedStateFromError(error: Error) {
    return { error };
  }

  public static getDerivedStateFromProps(props: ErrorBoundaryProps, state: ErrorBoundaryState): Partial<ErrorBoundaryState> | null {
    if (props.mountKey !== state.mountKey) {
      return state.error ? { error: undefined, mountKey: props.mountKey } : { mountKey: props.mountKey };
    }
    return null;
  }

  public render() {
    const { children } = this.props;
    const { error } = this.state;

    if (!error) {
      return children;
    }

    return <ErrorBoundaryComponent error={error} />;
  }

  public componentDidCatch(error: Error) {
    this.handleError(error);
  }

  private handleError(error: Error) {
    if (isAbortError(error)) {
      return;
    }

    this.props.onError?.(error);
  }
}

interface ErrorBoundaryComponentProps {
  error: Error;
}

function ErrorBoundaryComponent(props: ErrorBoundaryComponentProps) {
  const { error } = props;

  const [showDialog, setShowDialog] = useState(false);

  if (error instanceof DynamicImportError) {
    return (
      <>
        <ErrorContainer>
          <Illustration height="100%" src={Illustrations.Update} style={{ maxHeight: "200px", maxWidth: "200px" }} width="100%" />
          <h3>Updates are ready!</h3>
          <p>Looks like you&apos;ll need to refresh the page to go further.</p>
          <Button label="Refresh the page" size="large" tone="primary" onClick={handleRefresh} />
        </ErrorContainer>
      </>
    );
  }

  return (
    <>
      <ErrorContainer>
        <Illustration height="100%" src={Illustrations.OhNo} style={{ maxHeight: "300px", maxWidth: "300px" }} width="100%" />
        {getErrorMessage(error)}
        <Button label="Show me nerd stuff" onClick={handleDetailsOpen} />
      </ErrorContainer>
      <ErrorDetailsDialog error={showDialog ? error : undefined} onClose={handleDetailsClose} />
    </>
  );

  function handleDetailsOpen() {
    setShowDialog(true);
  }

  function handleDetailsClose() {
    setShowDialog(false);
  }
}

function handleRefresh() {
  window.location.reload();
}
